import { Component, DestroyRef, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { API, CompanyDTO, PlanningDTO } from '../../../../services/api/api';
import { ErrorService } from '../../../../services/error.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { UtilService } from '../../../../services/util.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CompaniesComponent } from '../../../company/companies/companies.component';
import { UserService } from '../../../../services/user.service';
import { RoutingID } from '../../../../routes/sb-routing-enum';
import { NavigationService } from '@ngx-seatback/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';

@Component({
  selector: 'sb-page-planning-edit',
  templateUrl: './planning-edit.component.html',
  styleUrls: ['./planning-edit.component.scss'],
  standalone: false,
})
export class PlanningEditComponent implements OnInit {
  planning: PlanningDTO;
  planningId: number;
  loading: boolean;
  customer: CompanyDTO;
  form: UntypedFormGroup;

  constructor(
    private activatedRoute: ActivatedRoute,
    private api: API,
    public errorHandler: ErrorService,
    public modal: ModalService,
    public util: UtilService,
    private userService: UserService,
    public navService: NavigationService,
    private destroyRef: DestroyRef
  ) {
    this.planning = undefined;
    this.planningId = null;
    this.loading = true;
    this.customer = undefined;
    this.form = new UntypedFormGroup({
      Name: new UntypedFormControl('', [Validators.required]),
      Description: new UntypedFormControl('', []),
    });
  }

  get Name() {
    return this.form.get('Name');
  }

  get Description() {
    return this.form.get('Description');
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.planningId = params.planningID;
      if (!this.planningId) {
        this.userService.selectedCompany.subscribe((company: CompanyDTO) => {
          if (company) {
            this.customer = company;
          }
        });
      }
      this.refreshFromServer();
    });
    this.registerFormChangesListener();
  }

  private registerFormChangesListener() {
    this.Name.valueChanges.subscribe(value => {
      this.planning.name = value;
    });
    this.Description.valueChanges.subscribe(value => {
      this.planning.description = value;
    });
  }

  updateForm() {
    this.Name.setValue(this.planning.name);
    this.Name.markAsTouched();
    this.Description.setValue(this.planning.description);
    this.Description.markAsTouched();
  }

  nextStep(wizard: any, nextTab: any) {
    wizard.selectTab(nextTab);
  }

  private refreshFromServer() {
    this.loading = true;
    if (!this.planningId) {
      this.planning = new PlanningDTO();
      this.loading = false;
    } else {
      this.api
        .ds_cm_Planning_retrieveById(this.planningId)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: data => {
            this.planning = data;
            this.updateForm();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  updatePlanningCall() {
    this.loading = true;
    if (!this.planning.id) {
      if (this.customer) {
        this.api
          .ds_cm_Planning_createPlanningForCustomer(this.customer.id, this.planning)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            take(1),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe({
            next: (receivedPlanning: PlanningDTO) => {
              this.planning = receivedPlanning;
              this.navService.navigateToPoint(RoutingID.planning, [{ id: 'planningID', value: this.planning?.id }]);
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      } else {
        this.api
          .ds_cm_Planning_createPlanning(this.planning)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            take(1),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe({
            next: (receivedPlanning: PlanningDTO) => {
              this.planning = receivedPlanning;
              this.navService.navigateToPoint(RoutingID.planning, [{ id: 'planningID', value: this.planning?.id }]);
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    } else {
      this.api
        .ds_cm_Planning_editPlanning(this.planning)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (receivedPlanning: PlanningDTO) => {
            this.planning = receivedPlanning;
            this.navService.navigateToPoint(RoutingID.planning, [{ id: 'planningID', value: this.planning?.id }]);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  selectCompany() {
    this.modal
      .showNavigationModal({
        entryComponent: { value: CompaniesComponent, data: {} },
        title: null,
        modalOptions: { windowClass: 'bigXL' },
      })
      .then((result: CompanyDTO) => {
        if (result) {
          this.customer = result;
        } else {
          this.customer = null;
        }
      })
      .catch(() => {});
  }
}
